import type { Color, Group, NodeInterface } from './types'
import type { Settings } from './settings'
import * as PIXI from 'pixi.js'
import type { ImageStore } from 'visualizations/image-store'

export class Node implements NodeInterface {
  public id: string
  public blobId?: string
  public group?: Group
  public size?: number
  public colors: Color[]
  public index?: number
  public x?: number
  public y?: number
  public vx?: number
  public vy?: number
  public graphics?: PIXI.Graphics
  public segmentMap: SegmentMap

  constructor (node: NodeInterface, segmentMap: SegmentMap) {
    this.segmentMap = segmentMap
    this.id = node.id
    this.blobId = node.blobId
    this.size = node.size
    this.x = node.x
    this.y = node.y
    this.radius = this.settings.radius(node.size)
    this.collisionRadius = this.radius * 1.1
    this.cropRadius = this.radius * 0.9
    this.imageSize = this.radius * 1.8

    if (node.groupId !== undefined && node.groupId !== null) {
      this.group = this.settings.groups.find(group => group.id === node.groupId)
    } else {
      this.group = this.settings.defaultGroup
    }

    if (node.colorIds !== undefined && node.colorIds !== null) {
      const colors = node.colorIds.map(colorId => {
        const color = this.settings.colors.find(color => color.id === colorId)
        return color !== undefined ? color : this.settings.defaultColor
      })
      this.colors = colors.length > 0 ? colors : [this.settings.defaultColor]
    } else {
      this.colors = [this.settings.defaultColor]
    }
  }

  public initializeGraphics (container: PIXI.Container, pointerdown: () => void, pointerover: () => void, pointerout: () => void): void {
    this.graphics = new PIXI.Graphics()
    this.graphics.x = this.x
    this.graphics.y = this.y
    this.graphics.eventMode = 'dynamic'
    this.graphics.on('pointerdown', pointerdown)
    this.graphics.on('pointerover', pointerover)
    this.graphics.on('pointerout', pointerout)
    container.addChild(this.graphics)
    this.draw()
  }

  private draw (): void {
    if (this.colors.length === 1) {
      this.circle()
    } else {
      this.arcs()
    }
    if (this.hasImage) {
      this.imageBackground()
    }
  }

  private circle (): void {
    this.graphics.beginFill(this.firstColor.colorInt)
    this.graphics.drawCircle(0, 0, this.radius)
    this.graphics.endFill()
  }

  private arcs (): void {
    this.colors.map(color => color.colorInt).forEach((color: number, index: number) => {
      this.graphics.beginFill(color)
      this.graphics.arc(0, 0, this.radius, index * this.arcLength, (index + 1) * this.arcLength)
      this.graphics.lineTo(0, 0)
      this.graphics.endFill()
    })
  }

  private imageBackground (): void {
    this.graphics.beginFill(0xffffff)
    this.graphics.drawCircle(0, 0, this.cropRadius)
    this.graphics.endFill()
  }

  public loadImage (): void {
    this.imageStore.getBlob(this.blobId)
      .then(blob => {
        if (blob !== null && blob !== undefined) {
          const url = URL.createObjectURL(blob)
          const texture = PIXI.Texture.from(url)
          const sprite = new PIXI.Sprite(texture)
          sprite.width = this.imageSize
          sprite.height = this.imageSize
          sprite.anchor.set(0.5)
          this.graphics.addChild(sprite)
          URL.revokeObjectURL(url)
        } else {
          // TODO Implement this
        }
      })
      .catch(error => {
        console.error(error)
      })
  }

  get settings (): Settings {
    return this.segmentMap.settings
  }

  get imageStore (): ImageStore {
    return this.segmentMap.imageStore
  }

  get arcLength (): number {
    return (2 * Math.PI) / this.colors.length
  }

  get firstColor (): number {
    return this.colors[0]
  }

  get hasImage (): boolean {
    return this.blobId !== undefined && this.blobId !== null
  }
}
